import { Entity } from '@api/api/entity-schema';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { ErrorMessageWrapper } from '@terminal/components/error-message-wrapper/ErrorMessageWrapper';
import { config } from '@terminal/config/app.config';
import { useEarningsInsightsByEntities } from '@terminal/react-query-hooks/earnings/useEarningsInsightsByEntities/useEarningsInsightsByEntities';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { EarningsInsightPill } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/components/earnings-insight-pill/EarningsInsightPill';
import { PreviewContent } from '@terminal/widgets/knowledge-graph-widget/components/earnings-insight-section/PreviewContent';
import { RecapContent } from '@terminal/widgets/knowledge-graph-widget/components/earnings-insight-section/RecapContent';
import { useTranslation } from 'react-i18next';
import styles from './EarningsSection.module.scss';

export interface EarningsSectionProps {
  entity: Entity;
}

export const EarningsSection = ({ entity }: EarningsSectionProps) => {
  const { t } = useTranslation(['widget']);
  const {
    byEntityTag,
    isLoading: isEarningsLoading,
    isError,
  } = useEarningsInsightsByEntities([entity.tag]);

  const {
    latestEarningsData: earningsData,
    earningsInsight,
    isPreview = false,
  } = byEntityTag.get(entity.tag) ?? {};

  if (isEarningsLoading) {
    return (
      <div className={styles.container}>
        <SkeletonLoader
          areas={[
            {
              styles: { height: '100px', width: '100%', borderRadius: '8px' },
            },
          ]}
        />
      </div>
    );
  }

  if (isError) {
    throw new Error('AO > Error fetching earnings insights');
  }

  const title = earningsData
    ? `${earningsData.fiscal_period} ${earningsData.fiscal_year} ${t(
        'widget:assetOverview.earningsSection.earnings'
      )}`
    : t('widget:assetOverview.earningsSection.upcomingEarnings');
  return (
    <div>
      <div className={styles.titleContainer}>
        <h3 className={styles.title}>{title}</h3>
        {earningsInsight && (
          <EarningsInsightPill
            isPreview={isPreview}
            entityTag={entity.tag}
            earningsInsight={earningsInsight}
            source={POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW}
            isClickable={!config.app.isTeamsApp}
          />
        )}
      </div>
      <div className={styles.container}>
        {earningsData ? (
          <div className={styles.content}>
            {isPreview ? (
              <PreviewContent earningsData={earningsData} />
            ) : (
              <RecapContent earningsData={earningsData} entity={entity} />
            )}
          </div>
        ) : (
          <ErrorMessageWrapper variant="empty" displayIcon showBorder={false} />
        )}
      </div>
    </div>
  );
};
